import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { FeatureService } from 'app/library/feature/services/feature.service';
import {
    AudienceMembersActionsService,
    IAudienceMembersActionsContext,
    IAudienceMembersActionsPermissions,
} from 'app/library/grow/services/audience-members-actions.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { AddUsers } from 'app/projects/user/src/lib/actions/add-entities.action';
import { RefreshUsersOnlineState } from 'app/projects/user/src/lib/actions/refresh-users-online-state.action';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-automatic-audience-members-list',
    templateUrl: './automatic-audience-members-list.component.html',
    styleUrls: ['./automatic-audience-members-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AutomaticAudienceMembersListComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input()
    audienceId = '';

    @Input()
    audienceName = '';

    @Input()
    reloadToken = 0;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;

    @ViewChild(MatSort)
    sort: MatSort;

    searchInput = new FormControl('');
    users: User[] = [];
    totalCount = 0;
    isLoading = false;
    displayedColumns = ['checkbox', 'avatar', 'firstName', 'lastName', 'email', 'buttons'];
    selectedUserIds: string[] = [];
    checkboxes: Record<string, boolean> = {};

    sortBy = 'id';
    sortOrder: SortDirection = 'asc';
    isUserDetailsCardHidden = false;
    actionPermissions: IAudienceMembersActionsPermissions;

    private _loadMembers$ = new Subject<void>();
    private _viewReady = false;
    private _onlineStateInterval: ReturnType<typeof setInterval>;
    private _lastLoadKey = '';
    private _lastOnlineStateUserIds = '';

    get hasSearchQuery(): boolean {
        return !!this.searchInput.value?.trim();
    }

    get showNoSearchResults(): boolean {
        return !this.isLoading && this.hasSearchQuery && this.users.length === 0;
    }

    get hasNoUsers(): boolean {
        return this.totalCount === 0;
    }

    get actionsUserCount(): number {
        return this.selectedUserIds.length || this.totalCount;
    }

    get isAllSelected(): boolean {
        if (!this.users.length) {
            return false;
        }

        return this.users.every((user) => this.selectedUserIds.includes(user.id));
    }

    get isIndeterminateSelection(): boolean {
        return this.selectedUserIds.length > 0 && !this.isAllSelected;
    }

    constructor(
        private _audienceService: AudienceService,
        private _audienceMembersActionsService: AudienceMembersActionsService,
        private _store: Store,
        private _featureService: FeatureService,
        private _cd: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.actionPermissions = this._audienceMembersActionsService.getPermissions();
    }

    goToUserProfile(userId: string): void {
        this.isUserDetailsCardHidden = true;
        this._store.dispatch(new Navigate(['/users/' + userId + '/edit']));
    }

    ngAfterViewInit(): void {
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe(() => {
            this.clearSelection();
            if (this.paginator) {
                this.paginator.pageIndex = 0;
            }
            this._scheduleLoad();
        });

        this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
            this.sortBy = sort.active;
            this.sortOrder = sort.direction;
            if (this.paginator) {
                this.paginator.pageIndex = 0;
            }
            this._scheduleLoad();
        });

        this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.clearSelection();
            this._scheduleLoad();
        });

        this._loadMembers$.pipe(debounceTime(0), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadMembers();
        });

        this._viewReady = true;
        this._scheduleLoad();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this._viewReady) {
            return;
        }

        if (changes.reloadToken && !changes.reloadToken.firstChange) {
            if (this.paginator) {
                this.paginator.pageIndex = 0;
            }
            this.clearSelection();
        }

        if (changes.audienceId) {
            this._lastLoadKey = '';
            this._lastOnlineStateUserIds = '';
            this.clearSelection();
        }

        if (changes.audienceId || changes.reloadToken) {
            this._scheduleLoad();
        }
    }

    ngOnDestroy(): void {
        clearInterval(this._onlineStateInterval);
        this._loadMembers$.complete();
        super.ngOnDestroy();
    }

    toggleUserSelection(user: User): void {
        if (this.selectedUserIds.includes(user.id)) {
            this.selectedUserIds = this.selectedUserIds.filter((id) => id !== user.id);
            this.checkboxes[user.id] = false;
        } else {
            this.selectedUserIds = [...this.selectedUserIds, user.id];
            this.checkboxes[user.id] = true;
        }
    }

    masterToggle(): void {
        if (this.isAllSelected) {
            this.clearSelection();
            return;
        }

        this.selectedUserIds = this.users.map((user) => user.id);
        this.checkboxes = this.users.reduce((acc, user) => {
            acc[user.id] = true;
            return acc;
        }, {} as Record<string, boolean>);
    }

    clearSelection(): void {
        this.selectedUserIds = [];
        this.checkboxes = {};
    }

    downloadUsers(): void {
        this._audienceMembersActionsService.downloadUsers(this._buildActionsContext());
    }

    openPasswordlessAccessDialog(userIds?: string[]): void {
        void this._audienceMembersActionsService.openPasswordlessAccessDialog(this._buildActionsContext(), userIds);
    }

    openAssignUserToTeamDialog(userIds?: string[]): void {
        void this._audienceMembersActionsService.openAssignUserToTeamDialog(this._buildActionsContext(), userIds);
    }

    assignUsersToExperience(userIds?: string[]): void {
        void this._audienceMembersActionsService.assignUsersToExperience(this._buildActionsContext(), userIds);
    }

    assignUsersToAudience(userIds?: string[]): void {
        void this._audienceMembersActionsService.assignUsersToAudience(this._buildActionsContext(), userIds);
    }

    assignUsersToCourse(userIds?: string[]): void {
        void this._audienceMembersActionsService.assignUsersToCourse(this._buildActionsContext(), userIds);
    }

    resetPasswords(userIds?: string[]): void {
        void this._audienceMembersActionsService.resetPasswords(this._buildActionsContext(), userIds);
    }

    removeUsers(userIds?: string[]): void {
        this._audienceMembersActionsService.removeUsers(this._buildActionsContext(), userIds);
    }

    private _buildActionsContext(): IAudienceMembersActionsContext {
        return {
            audienceId: this.audienceId,
            audienceName: this.audienceName,
            searchQuery: this.searchInput.value,
            selectedUserIds: this.selectedUserIds,
            totalCount: this.totalCount,
            onAfterAction: () => {
                this.clearSelection();
                this._scheduleLoad();
            },
        };
    }

    private _scheduleLoad(): void {
        setTimeout(() => this._loadMembers$.next());
    }

    loadMembers(): void {
        if (!this.audienceId || !this.paginator || !this.sort) {
            return;
        }

        const sortBy = this.sort.active || this.sortBy;
        const sortOrder = this.sort.direction || this.sortOrder;
        const loadKey = [this.audienceId, this.paginator.pageIndex, this.paginator.pageSize, sortBy, sortOrder, this.searchInput.value?.trim() ?? ''].join('|');

        if (loadKey === this._lastLoadKey && this.isLoading) {
            return;
        }

        this._lastLoadKey = loadKey;
        this.isLoading = true;

        this._audienceService
            .fetchAudienceMembers(this.audienceId, {
                page: this.paginator.pageIndex + 1,
                limit: this.paginator.pageSize,
                sortBy,
                sortOrder,
                query: this.searchInput.value?.trim() || undefined,
            })
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (response) => {
                    this.users = response.data.map((query) => new User(query));
                    this.totalCount = response.pagination.number_of_results;
                    this.checkboxes = this.users.reduce((acc, user) => {
                        acc[user.id] = this.selectedUserIds.includes(user.id);
                        return acc;
                    }, {} as Record<string, boolean>);
                    this._refreshOnlineState(this.users);
                },
                error: () => {
                    this.users = [];
                    this.totalCount = 0;
                    clearInterval(this._onlineStateInterval);
                },
            });
    }

    private _refreshOnlineState(users: User[]): void {
        if (!this._featureService.checkFeatureStatus(FeatureSwitchName.UserOnlineState) || !users.length) {
            return;
        }

        const userIdsKey = users
            .map((user) => user.id)
            .sort()
            .join(',');

        if (userIdsKey === this._lastOnlineStateUserIds) {
            return;
        }

        this._lastOnlineStateUserIds = userIdsKey;
        clearInterval(this._onlineStateInterval);
        this._store.dispatch(new AddUsers(users));
        this._store.dispatch(new RefreshUsersOnlineState(users)).subscribe(() => {
            this._syncUsersOnlineStateFromStore();
        });

        this._onlineStateInterval = setInterval(() => {
            if (!this.users.length) {
                return;
            }

            this._store.dispatch(new RefreshUsersOnlineState(this.users)).subscribe(() => {
                this._syncUsersOnlineStateFromStore();
            });
        }, 30000);
    }

    private _syncUsersOnlineStateFromStore(): void {
        if (!this.users.length) {
            return;
        }

        const userState = this._store.selectSnapshot(UserState);

        this.users = this.users.map((user) => {
            const storedUser = UserState.getEntity(userState, user.id);

            if (!storedUser) {
                return user;
            }

            return Object.assign(new User(), user, { isOnline: storedUser.isOnline });
        });

        this._cd.markForCheck();
    }
}
